import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBarRef } from '@angular/material/snack-bar';
import { Observable, Subject, Subscription, forkJoin, of } from 'rxjs';
import { catchError, map, take, takeUntil } from 'rxjs/operators';
import { TimeCategory } from 'src/app/dialogs/clinical-time-dialog.component';
import {
  CarePlanPickerPlan,
  CarePlanPickerStatus,
  CarePlanWorkErrorCode,
  CarePlanWorkRequest,
} from 'src/app/models/ccm/care-plan-work.model';
import { CLINICAL_TIME_LEFT_FOR_CARE_PLAN, ClinicalTimeRestore } from 'src/app/models/ccm/ccm-pending-time.model';
import {
  CLINICAL_NOTE_ERROR_CODES,
  CLINICAL_TIME_KEPT_FOR_NOTE,
  ClinicalNoteErrorCode,
  LinkedClinicalNote,
} from 'src/app/models/clinical-time-note.model';
import { ClinicalTime, ContactStatus } from 'src/app/models/clinical.model';
import { Diagnosis } from 'src/app/models/fhir-models';
import { NewAuthService } from 'src/app/services/auth/new-auth-service.service';
import { CarePlanWorkService } from 'src/app/services/ccm/care-plan-work.service';
import { CcmCarePlanReturnService } from 'src/app/services/ccm/ccm-care-plan-return.service';
import {
  CARE_PLAN_WORK_ERROR_CODES,
  emptyCarePlanWork,
  validateCarePlanWork,
  visiblePicks,
} from 'src/app/services/ccm/care-plan-work.utils';
import { ChartDraftService } from 'src/app/services/chart-draft/chart-draft.service';
import { ClientsService } from 'src/app/services/clients/clients.service';
import { ClinicalNoteCacheService } from 'src/app/services/clinical-note-cache.service';
import { DataService } from 'src/app/services/data.service';
import { FirestoreService } from 'src/app/services/firestore.service';
import { MeasureSummaryService } from 'src/app/services/measures/measure-summary.service';
import { PatientTimerService } from 'src/app/services/patient-timer.service';
import { PatientService } from 'src/app/services/patient.service';
import { ProgramService } from 'src/app/services/programs/program.service';
import { SnackService } from 'src/app/services/snack.service';
import {
  ShortCallCheckResult,
  ShortCallCheckService,
  isSpokeAnswer,
} from 'src/app/services/calls/short-call-check.service';
import { AreYouSureModalComponent } from '../../are-you-sure-modal/are-you-sure-modal.component';
import { ClinicalTimeSaveOutcome, WelbyClinicalTimeConf } from '../../models/clinical-time-save.model';
import { serverErrorMessage } from '../../utils/server-error-message';
import { atLeastOneFieldValidator } from '../../validators/one-field-filled-at-least';

/** A time row's seconds run 0 to 59: 60 or more is a minute. */
const MAX_SECONDS = 59;

/** Refusals that mean today's care plans differ from the ones on screen: the picker reloads them. */
const CARE_PLAN_RELOAD_CODES: CarePlanWorkErrorCode[] = ['PLAN_NOT_ACTIVE', 'GOAL_NOT_OPEN', 'NO_INTERVENTIONS'];
/** Refusals no change to the picks can fix: saving again from the same form fails the same way. */
const CARE_PLAN_REOPEN_CODES: CarePlanWorkErrorCode[] = ['MALFORMED', 'PATIENT_CHANGED', 'INVALID_TIME_ID'];
/** How much of the linked note the form shows, as plain text. */
const NOTE_PREVIEW_LENGTH = 240;
/**
 * A minutes or seconds field kept for the way back from Care Plans, in two digits as the form shows them on
 * opening: the number inputs hand a typed "00" over as 0. An emptied field — null from those inputs — stays
 * empty, so it still asks to be filled.
 */
const timeFieldAsShown = (value: unknown): string =>
  value === null || value === undefined || value === '' ? '' : String(value).padStart(2, '0');

/** Shown when a save fails and the server gives no reason. */
export const TIME_SAVE_FAILED_MESSAGE = "Couldn't save this time entry. Please try again.";

/** Shown when some rows of a new entry saved and others failed; only the failed rows stay in the form. */
export const partialTimeSaveMessage = (saved: number, total: number, reason: string): string =>
  `${saved} of ${total} entries saved. The rows still in the form were not: ${reason}`;

@Component({
  selector: 'app-clinical-time-save-form',
  templateUrl: './clinical-time-save-form.component.html',
  styleUrls: ['./clinical-time-save-form.component.scss'],
})
export class ClinicalTimeSaveFormComponent implements OnInit, OnDestroy {
  @Input() data;
  @Output() closeDialog = new EventEmitter<boolean>();
  form: UntypedFormGroup;
  delay = 1000;
  categories: TimeCategory[] = [];
  programs: any[] = [];
  loading = false;
  currentMonthlyTime: any;
  currentTimes = {};
  welbyConf: WelbyClinicalTimeConf;
  unsubscribe$ = new Subject<void>();
  diagnoses: Diagnosis[] = [];

  /**
   * CCM care plan time (BRD-CLIN-2026-008): on only once the patient's clinic reads as switched on. Until
   * then every row's `care_plan_work` control stays disabled, so the form, its value and what it saves
   * are exactly what they were before the feature.
   */
  carePlanTimeOn = false;
  carePlanStatus: CarePlanPickerStatus = 'loading';
  /** One list for every CCM row, kept as the same reference until a reload replaces it. */
  carePlanPlans: CarePlanPickerPlan[] = [];
  /** False when editing an entry already CCM before go live with no saved picks: it may be saved without (FR-5.3). */
  carePlanRequiredOnEditedRow = true;
  /** True while we look up (and may wait up to 9 seconds for) the length of the call behind the entry. */
  checkingCall = false;
  /** On an edit, the row the form opened with: the stored entry. Kept by identity, so deleting rows cannot shift it. */
  private editedTimeRow: AbstractControl | null = null;
  private firstRowAdded = false;
  private carePlanPrefilled = false;
  /**
   * Back from building a care plan, every row brings the items it was left with — care coordination and
   * other included — so an edit's saved picks must not overwrite them once the care plans load.
   */
  private restoredCarePlanWork = false;
  /**
   * Set once the server refuses a row with `REQUIRED`. It reads the switch and FR-5.3 on its own and only
   * it sends that code, so its answer counts over the form's reading: picks are then required on every row.
   */
  private serverRequiresPicks = false;
  /** The care plan load in flight or done; null until the first CCM row of a switched-on clinic asks for it. */
  private carePlanLoad: Subscription | null = null;
  /**
   * The row each save call was built from, to tell which rows went through when some did not. Keyed by the
   * call itself: `HttpClient` gives every request its own Observable.
   */
  private rowOfCall = new WeakMap<Observable<unknown>, AbstractControl>();
  /** The refusal on screen, dismissed with the form. */
  private refusalSnack: MatSnackBarRef<unknown> | null = null;
  private destroyed = false;
  /** The server refused the note for a reason a retry will not change, so even a form that must be saved can be cancelled. */
  private noteRefusedForGood = false;
  /** A save of a form that must be saved failed: the nurse may cancel and keep the minutes instead of being stuck. */
  private saveFailed = false;
  private notePreview?: string;
  private shortCallCheckSub: Subscription | null = null;

  constructor(
    private fb: UntypedFormBuilder,
    private patientService: PatientService,
    private auth: NewAuthService,
    private snackBar: SnackService,
    private dataService: DataService,
    private activePrograms: ProgramService,
    private timeService: PatientTimerService,
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<ClinicalTimeSaveFormComponent>,
    private cacheNote: ClinicalNoteCacheService,
    private firebaseService: FirestoreService,
    private measureService: MeasureSummaryService,
    private chartDraftService: ChartDraftService,
    private clientsService: ClientsService,
    private carePlanWorkService: CarePlanWorkService,
    private carePlanReturn: CcmCarePlanReturnService,
    private shortCallCheck: ShortCallCheckService
  ) {}

  /** Rows of the programs grid: it fills down the first column, so that column holds the extra program. */
  get programRows(): number {
    return Math.max(1, Math.ceil(this.programs.length / 2));
  }

  /**
   * Why Save is off when a CCM row has nothing to pick from and nothing ticked (mockup screen 4): no care
   * plan, or care plans with no open goal. Empty otherwise, which leaves the button without a tooltip. Its
   * wrapper takes keyboard focus while it has a hint; the picker's status line says the same to screen readers.
   */
  get saveHint(): string {
    const work = this.times.controls.map((row) => row.get('care_plan_work')).filter((control) => control?.enabled);
    if (work.some((control) => control.hasError('noActiveCarePlan'))) {
      return 'Create a care plan, or tick Care coordination or Other and write the note';
    }
    const noOpenGoal = !this.carePlanPlans.some((plan) => plan.goals.length > 0);
    return noOpenGoal && work.some((control) => control.hasError('carePlanWorkRequired'))
      ? 'Add a goal to a care plan, or tick Care coordination or Other and write the note'
      : '';
  }

  /**
   * Whether a CCM row with nothing to pick can take the nurse to the Care Plans tab and back (S2.4).
   * Only from the openers that pass an `origin` — the timer's leave modal has no way back to the form —
   * and not while another entry already waits on a care plan.
   */
  get canCreateCarePlan(): boolean {
    return (
      this.carePlanTimeOn && ['timer', 'summary', 'edit'].includes(this.data?.origin) && !this.carePlanReturn.pending
    );
  }

  /**
   * At a clinic with CCM care plan time on, a new entry covers the clinical note the nurse saved first:
   * the note form opens this form with it. An edit keeps the note it was saved with.
   */
  get noteRequired(): boolean {
    return this.carePlanTimeOn && this.data?.isNew === true;
  }

  get linkedNote(): LinkedClinicalNote | undefined {
    return this.data?.linkedNote?.id ? this.data.linkedNote : undefined;
  }

  /** Save stays off until the entry has its note; the server refuses it without one. */
  get missingNote(): boolean {
    return this.noteRequired && !this.linkedNote;
  }

  /**
   * Cancel is hidden on a form that must be saved, unless saving cannot succeed: there is no note, the
   * server refused the note for a reason a retry will not change, or a save failed for a reason the form
   * cannot tell apart (e.g. a monthly cap). Cancelling still keeps the timer's minutes.
   */
  get canCancel(): boolean {
    return !this.data?.required || this.missingNote || this.noteRefusedForGood || this.saveFailed;
  }

  /** The linked note as plain text, cut short: its HTML is never rendered here. Worked out once, as the note does not change. */
  get linkedNotePreview(): string {
    this.notePreview ??= this.plainTextPreview(this.linkedNote?.content ?? '');
    return this.notePreview;
  }

  ngOnInit() {
    this.getActivePrograms();
    this.getCurrentMonthlyTime();
    this.categories = this.dataService.categories;
    // Edits open with the entry's saved values; new entries start blank. The contact choice is required
    // and has no default, so the clinician always answers it.
    const savedTime = this.data?.time;
    this.form = this.fb.group({
      times: this.fb.array([], Validators.required),
      exclude: [savedTime?.excluded ?? false, []],
      contact_status: [this.initialContactStatus(savedTime), [Validators.required]],
      require_provider_intervention: [savedTime?.require_provider_intervention ?? false, []],
      device_troubleshooting: [savedTime?.device_troubleshooting ?? false, []],
    });
    // Back from building a care plan (S2.4): every row and flag as the nurse left them.
    const restore: ClinicalTimeRestore | undefined = this.data?.restore;
    if (restore?.rows?.length) {
      this.restoredCarePlanWork = true;
      restore.rows.forEach((row, index) =>
        this.addTime(
          row.mins,
          row.secs,
          row.categories,
          !this.data?.isNew && index === restore.editedRowIndex,
          row.care_plan_work
        )
      );
      this.form.patchValue(restore.flags ?? {});
    } else {
      this.addTime(
        this.data?.time?.mins?.toString()?.padStart(2, '0'),
        this.data?.time?.secs?.toString()?.padStart(2, '0'),
        this.data.time.categories
      );
    }
    this.patientService
      .getPatientDiagnoses(this.data.time.patient_id)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((diagnoses) => {
        this.diagnoses = diagnoses;
        this.setCheckboxState();
      });
    if (this.data.id) {
      this.currentTimes[this.data.id] = this.data.time; // If the component is in edit mode, we need to store the current time to check if the time is out of the limit
    }
    this.getWelbyConf();
    if (!this.data?.time?.categories && !restore?.rows?.length) {
      this.updateCheckboxes();
    }
    this.setUpCarePlanTime();
  }

  /**
   * The contact choice to pre-select when editing. Entries saved before the three-way choice only carry
   * the old checkbox: a tick reads as `spoke`; anything else is left unanswered so it must be picked again.
   */
  initialContactStatus(savedTime?: Partial<ClinicalTime>): ContactStatus | null {
    if (savedTime?.contact_status) {
      return savedTime.contact_status;
    }
    return savedTime?.contact === true ? 'spoke' : null;
  }

  setCheckboxState() {
    const rpmChecked = this.diagnoses.some((diagnosis) => diagnosis.association?.rpm === true);
    const pcmChecked = this.diagnoses.some((diagnosis) => diagnosis.association?.pcm === true);
    const bhiChecked = this.diagnoses.some((diagnosis) => diagnosis.association?.bhi === true);
    const ccmChecked = this.diagnoses.filter((diagnosis) => diagnosis.association?.ccm === true).length >= 2;

    if (!rpmChecked) {
      this.times.controls[0].get('categories').get('rpms')?.disable();
      this.times.controls[0].get('categories').get('rpms').setValue(false);
    } else {
      this.times.controls[0].get('categories').get('rpms')?.enable();
    }

    if (!pcmChecked) {
      this.times.controls[0].get('categories').get('pcm')?.disable();
      this.times.controls[0].get('categories').get('pcm').setValue(false);
    } else {
      this.times.controls[0].get('categories').get('pcm')?.enable();
    }

    if (!bhiChecked) {
      this.times.controls[0].get('categories').get('bhi')?.disable();
      this.times.controls[0].get('categories').get('bhi').setValue(false);
    } else {
      this.times.controls[0].get('categories').get('bhi')?.enable();
    }

    if (!ccmChecked) {
      this.times.controls[0].get('categories').get('ccm')?.disable();
      this.times.controls[0].get('categories').get('ccm').setValue(false);
    } else {
      this.times.controls[0].get('categories').get('ccm')?.enable();
    }
  }

  updateCheckboxes(): void {
    this.categories.forEach((program) => {
      const checkboxControl = this.times.controls[0].get('categories').get(`${program.value}`);
      if (checkboxControl) {
        checkboxControl.setValue(program.value === this.data?.time?.programValue);
      }
    });
  }

  getWelbyConf(): void {
    this.measureService.getWelbyConf().subscribe((conf) => {
      this.welbyConf = conf;
    });
  }

  getCurrentMonthlyTime(): void {
    this.patientService
      .getCurrentMonthlyTime(this.patientService.currentPatientServiceID)
      .subscribe((time: { status: string; data: any }) => {
        this.currentMonthlyTime = time.data;
      });
  }

  getActivePrograms() {
    this.activePrograms
      .getPrograms()
      .pipe(take(1))
      .subscribe((programs) => {
        this.programs = programs.sort((a, b) => a.sortIndex - b.sortIndex);
      });
  }

  get times() {
    return this.form.controls['times'] as UntypedFormArray;
  }

  /**
   * Keeps at most one leading zero in the minutes, typed or pasted, e.g. 000005 shows 05 and 0120 shows 120.
   * The field starts as 00, so two digits are kept. There is no limit on the number of digits.
   */
  trimMinutes(control: AbstractControl, input: HTMLInputElement): void {
    let value = input.value;
    while (value.length > 2 && value.startsWith('0')) {
      value = value.slice(1);
    }
    if (value !== input.value) {
      input.value = value;
      control.setValue(Number(value));
    }
  }

  /** How many characters the minutes take, so the field can widen to show them all. */
  minutesLength(control: AbstractControl): number {
    return String(control.value ?? '').length;
  }

  /** Drops the typed digits that would take the seconds past 59, e.g. typing 7 then 1 keeps 7. */
  limitSeconds(control: AbstractControl, input: HTMLInputElement): void {
    let value = input.value;
    while (value.length > 2 || Number(value) > MAX_SECONDS) {
      value = value.slice(0, -1);
    }
    if (value !== input.value) {
      input.value = value;
      control.setValue(value === '' ? null : Number(value));
    }
  }

  /**
   * `editedRow` defaults to "the first row of an edit"; a restored entry says which row it is, and brings the
   * items the row was left with in `carePlanWork`.
   */
  addTime(
    mins?,
    secs?,
    categories?: any,
    editedRow = !this.data?.isNew && !this.firstRowAdded,
    carePlanWork?: CarePlanWorkRequest
  ) {
    this.firstRowAdded = true;
    const timeForm = this.fb.group({
      mins: [mins ?? '0', [Validators.compose([Validators.required, Validators.pattern(/^\d+$/)])]],
      secs: [
        secs ?? '0',
        [Validators.compose([Validators.required, Validators.pattern(/^[0-5]?\d$/), Validators.max(MAX_SECONDS)])],
      ],
      categories: this.fb.group(
        {
          rpms: [categories?.rpms ?? false, []],
          rpmc: [categories?.rpmc ?? false, []],
          ccm: [categories?.ccm ?? false, []],
          tcm: [categories?.tcm ?? false, []],
          chi: [categories?.chi ?? false, []],
          pin: [categories?.pin ?? false, []],
          bhi: [categories?.bhi ?? false, []],
          g0511: [categories?.g0511 ?? false, []],
          rtm: [categories?.rtm ?? false, []],
          pcm: [categories?.pcm ?? false, []],
          econsult: [categories?.econsult ?? false, []],
          visit: [categories?.visit ?? false, []],
          apcm: [categories?.apcm ?? false, []],
          awv: [categories?.awv ?? false, []],
          non_billable: [categories?.non_billable ?? false, []],
        },
        { validators: atLeastOneFieldValidator }
      ),
      // Disabled — out of the form's validity and value — unless the clinic is switched on and the row is CCM.
      care_plan_work: this.fb.control(
        { value: carePlanWork ?? emptyCarePlanWork(), disabled: true },
        this.carePlanValidator(editedRow)
      ),
    });
    if (editedRow) {
      this.editedTimeRow = timeForm;
    }
    (this.form.get('times') as UntypedFormArray).push(timeForm);
    timeForm
      .get('categories.ccm')
      .valueChanges.pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => this.syncCarePlanControl(timeForm));
    this.syncCarePlanControl(timeForm);
  }

  deleteTime(index) {
    (this.form.get('times') as UntypedFormArray).removeAt(index);
  }

  /** A row shows the care plan picker while its `care_plan_work` control is on. */
  showsCarePlanPicker(row: AbstractControl): boolean {
    return this.carePlanTimeOn && row.get('care_plan_work')?.enabled === true;
  }

  /** Only the row an edit opened with can be the entry FR-5.3 lets through; a row added in the form always needs picks. */
  requiresCarePlanPicks(row: AbstractControl): boolean {
    return this.requiresPicks(row === this.editedTimeRow);
  }

  /** The picker's "Try again" after a failed load. */
  retryCarePlans(): void {
    this.loadCarePlans();
  }

  /**
   * The picker's "Create care plan" (S2.4, FR-4.1, FR-4.2): keeps the whole entry — every row and the flags
   * under them — and takes the nurse to the patient's Care Plans tab, which brings them back here once the
   * plan is saved, as it was left. The chart timer is cleared as on any close, so nothing else saves the
   * same minutes again.
   */
  goToCarePlans(row: AbstractControl): void {
    const patient = this.patientService.currentPatientService;
    const dialogData = { ...this.data };
    delete dialogData.restore;
    const flags = this.form.getRawValue();
    this.dialogRef.close(CLINICAL_TIME_LEFT_FOR_CARE_PLAN);
    this.carePlanReturn.leave({
      patientId: this.patientService.currentPatientServiceID,
      patientName: [patient?.firstName, patient?.lastName].filter(Boolean).join(' '),
      dialogData,
      restore: {
        rows: this.times.getRawValue().map((time) => ({
          mins: timeFieldAsShown(time.mins),
          secs: timeFieldAsShown(time.secs),
          categories: { ...time.categories },
          care_plan_work: time.care_plan_work ?? emptyCarePlanWork(),
        })),
        flags: {
          exclude: flags.exclude === true,
          contact_status: flags.contact_status ?? null,
          require_provider_intervention: flags.require_provider_intervention === true,
          device_troubleshooting: flags.device_troubleshooting === true,
        },
        editedRowIndex: this.editedTimeRow ? this.times.controls.indexOf(this.editedTimeRow) : -1,
      },
      returnRowIndex: this.times.controls.indexOf(row),
      mode: this.carePlanPlans.length ? 'add-goals' : 'create',
    });
  }

  /**
   * Saves the entry. A NEW entry answered "spoke" is first checked against the call behind it: if
   * that call lasted under 30 seconds (ringing included) the clinician is asked to confirm. It is a
   * warning, never a block. Edits skip the check and keep whatever the entry already recorded.
   */
  saveTime(): void {
    if (!this.data?.isNew || !isSpokeAnswer(this.form.get('contact_status').value)) {
      this.saveTimeEntries();
      return;
    }
    this.loading = true;
    this.checkingCall = true;
    this.shortCallCheckSub?.unsubscribe();
    this.shortCallCheckSub = this.shortCallCheck
      .check(this.patientService?.currentPatientServiceID, this.auth.user?.user_id)
      .pipe(
        take(1),
        catchError(() => of<ShortCallCheckResult>({ fields: { short_call_check: 'no_call' }, isShort: false }))
      )
      .subscribe((result) => {
        this.checkingCall = false;
        this.shortCallCheckSub = null;
        if (!result.isShort) {
          this.saveTimeEntries(result.fields);
          return;
        }
        this.confirmShortCall(result.fields.call_duration_sec).subscribe((confirmed) => {
          if (confirmed) {
            this.saveTimeEntries({ ...result.fields, short_call_confirmed: true });
          } else {
            // "Change my answer": back to the form, nothing saved.
            this.loading = false;
          }
        });
      });
  }

  /** Asks whether a live conversation really happened on a short call. Emits true only for "Yes". */
  confirmShortCall(durationSec: number): Observable<boolean> {
    const seconds = `${durationSec} second${durationSec === 1 ? '' : 's'}`;
    return this.dialog
      .open(AreYouSureModalComponent, {
        data: {
          title: 'Short call',
          body: `That call lasted ${seconds} (including ringing). Did you speak live with the patient?`,
          hideCancelBtn: false,
          cancelButton: 'Change my answer',
          confirmButton: 'Yes, I spoke with them',
        },
        width: '500px',
      })
      .afterClosed()
      .pipe(map((result) => result?.confirmed === true));
  }

  private saveTimeEntries(shortCallFields: Partial<ClinicalTime> = {}): void {
    this.loading = true;
    const loadDate = this.firebaseService.timestamp;
    const times = this.times.getRawValue();
    const calls = [];
    const timesForm = [];
    const outLimitTimes = [];

    times.forEach((time, index) => {
      const loadTime = Number(time.mins) + Number(time.secs) / this.timeService.getOneMinuteInSeconds();
      const newTime: ClinicalTime = {
        minutes: loadTime,
        categories: {
          rpm: (time.categories.rpms || time.categories.rpmc) ?? false,
          rpms: time.categories.rpms ?? false,
          rpmc: time.categories.rpmc ?? false,
          ccm: time.categories.ccm ?? false,
          tcm: time.categories.tcm ?? false,
          pcm: time.categories.pcm ?? false,
          bhi: time.categories.bhi ?? false,
          pin: time.categories.pin ?? false,
          chi: time.categories.chi ?? false,
          rtm: time.categories.rtm ?? false,
          econsult: time.categories.econsult ?? false,
          visit: time.categories.visit ?? false,
          awv: time.categories.awv ?? false,
          apcm: time.categories.apcm ?? false,
          non_billable: time.categories.non_billable ?? false,
        },
        category: this.getCategoryForBilling(time.categories),
        timeStamp: this.data.isNew ? loadDate : this.data.time.timeStamp,
        last_update: new Date(),
        user_id: this.auth.user.user_id,
        patient_id: this.patientService?.currentPatientServiceID,
        excluded: this.form.get('exclude').value ?? false,
        contact_status: this.form.get('contact_status').value,
        contact: isSpokeAnswer(this.form.get('contact_status').value),
        require_provider_intervention: this.form.get('require_provider_intervention').value ?? false,
        device_troubleshooting: this.form.get('device_troubleshooting').value ?? false,
        is_billable:
          this.auth.user.roles.isWelbyClinical &&
          !time.categories.awv &&
          !time.categories.apcm &&
          !time.categories.non_billable,
        ...shortCallFields,
      };
      // Only a row whose picker is on carries care plan work; every other row is saved as it always was.
      if (this.times.at(index).get('care_plan_work')?.enabled) {
        newTime.care_plan_work = this.visibleWork(time.care_plan_work);
      }
      if (this.noteRequired && this.linkedNote) {
        newTime.clinical_note_id = this.linkedNote.id;
      }
      // If is editing time, we need to check if the time is out of the limit
      const prevMinutesValue = this.data.id
        ? Number(this.currentTimes[this.data.id]?.mins) +
          Number(this.currentTimes[this.data.id]?.secs) / this.timeService.getOneMinuteInSeconds()
        : 0;

      const categoryLimitMap = {
        rpm: { total: 'rpmTotal', conf: 'rpmConf' },
        rpms: { total: 'rpmTotal', conf: 'rpmConf' },
        rpmc: { total: 'rpmTotal', conf: 'rpmConf' },
        ccm: { total: 'ccmTotal', conf: 'ccmConf' },
        pcm: { total: 'pcmTotal', conf: 'pcmConf' },
        bhi: { total: 'bhiTotal', conf: 'bhiConf' },
        tcm: { total: 'tcmTotal', conf: 'tcmConf' },
      };

      const categoryMapping = categoryLimitMap[newTime.category];

      // If we have a mapping for this category, check if it exceeds the limit
      if (categoryMapping) {
        const currentTotal = this.currentMonthlyTime[categoryMapping.total];
        const limit = this.welbyConf?.[categoryMapping.conf]?.limit;

        if (currentTotal + newTime.minutes - prevMinutesValue > limit) {
          outLimitTimes.push(newTime);
        }
      }
      timesForm.push(newTime);
      const call = this.patientService.handleClinicalTime(newTime, this.data.isNew, this.data.id);
      this.rowOfCall.set(call, this.times.at(index));
      calls.push(call);
    });

    if (outLimitTimes.length > 0) {
      this.loading = false;

      const categoryGroups = {
        rpm: {
          categories: ['rpm', 'rpms', 'rpmc'],
          displayName: 'RPM',
        },
        ccm: {
          categories: ['ccm'],
          displayName: 'CCM',
        },
        pcm: {
          categories: ['pcm'],
          displayName: 'PCM',
        },
        bhi: {
          categories: ['bhi'],
          displayName: 'BHI',
        },
        tcm: {
          categories: ['tcm'],
          displayName: 'TCM',
        },
      };

      // Find which category group has exceeded limits
      let exceededCategory = null;
      for (const [key, group] of Object.entries(categoryGroups)) {
        if (outLimitTimes.some((time) => group.categories.includes(time.category))) {
          exceededCategory = group.displayName;
          break;
        }
      }

      // Create message based on exceeded category
      const message = exceededCategory
        ? `This patient has exceeded the monthly limit for ${exceededCategory} category, Are you sure to proceed? This is not a billable service`
        : 'This patient has exceeded the monthly limit, Are you sure to proceed? This is not a billable service';

      const snackRef = this.snackBar.genericSnackBar(message, ['warn-snackbar'], 8000, 'Yes, proceed');
      snackRef.onAction().subscribe(() => {
        this.saveArrayTimes(calls);
      });
    } else {
      this.saveArrayTimes(calls);
    }
  }

  /**
   * Saves the array of times using the provided calls and updates the UI accordingly.
   *
   * @param calls - An array of observables representing the save calls.
   */
  saveArrayTimes(calls: Observable<any>[]): void {
    this.loading = true;
    const rows = calls.map((call) => this.rowOfCall.get(call));
    // Each row's call settles on its own, so a refused save knows which rows went through; cancelling the
    // others on a first failure, as a plain forkJoin does, never undid a write the server had received.
    const outcomes$ = calls.map((call) =>
      call.pipe(
        map((): ClinicalTimeSaveOutcome => ({ saved: true })),
        catchError((error: unknown) => of<ClinicalTimeSaveOutcome>({ saved: false, error }))
      )
    );
    forkJoin(outcomes$).subscribe((outcomes) => {
      if (outcomes.some((outcome) => !outcome.saved)) {
        this.handleSaveFailures(outcomes, rows);
        return;
      }
      this.snackBar.genericSnackBar('Time saved', ['success-snackbar']);
      this.loading = false;
      // Notify that clinical time has been updated
      const changeType = this.data.isNew ? 'added' : 'modified';
      this.patientService.notifyClinicalTimeChange(changeType);
      // The work session is finalized — clear any preserved chart draft for this patient.
      this.deletePatientChartDraft();
      this.dialogRef.close(true);
      // A new entry that needed a note was saved with it: there is nothing left to offer.
      if (!this.noteRequired) {
        this.openClinicalDialogIfNoNoteWasAdded();
      }
    });
  }

  private deletePatientChartDraft(): void {
    const patientId = this.patientService?.currentPatientServiceID;
    if (!patientId) {
      return;
    }
    this.chartDraftService.deleteDraft(patientId).subscribe({
      error: (err) => console.error('[ChartDraft] Failed to delete draft after time save', err),
    });
  }

  getCategoryForBilling(categories: any): string {
    if (categories.rpms) {
      return 'rpms';
    } else if (categories.rpmc) {
      return 'rpmc';
    } else if (categories.bhi) {
      return 'bhi';
    } else if (categories.pcm) {
      return 'pcm';
    } else if (categories.awv) {
      return 'awv';
    } else if (categories.apcm) {
      return 'apcm';
    } else if (categories.non_billable) {
      return 'non_billable';
    } else {
      return 'ccm'; // by now, all the other categories are CCM (Just for billing purposes)
    }
  }

  async openClinicalDialogIfNoNoteWasAdded(): Promise<void> {
    const todayNotes = await this.patientService.getClinicalTodayAssessments(
      this.patientService.currentPatientServiceID
    );
    if (todayNotes.length === 0) {
      const dialogRef = this.dialog.open(AreYouSureModalComponent, {
        data: {
          title: 'Add Clinical Note',
          body: `<p>Do you want to save a clinical note for today?</p>`,
          hideCancelBtn: false,
          cancelButton: 'Cancel',
          confirmButton: 'Create Note',
        },
        width: '500px',
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          // FR-1.1: the note opens empty for every program. Caching it still marks the note as pending to save.
          this.cacheNote.saveClinicalNoteInCache({ assessor: this.auth.user.user_id, content: '' });
          this.patientService.toggleNoteView();
        }
      });
    }
  }

  /**
   * Where a new entry needs a note, cancelling keeps the timer's minutes so the nurse can write it first. Also
   * stops any wait for the call length, so a cancelled save never goes through.
   */
  cancel(): void {
    this.stopShortCallCheck();
    this.dialogRef.close(this.noteRequired ? CLINICAL_TIME_KEPT_FOR_NOTE : false);
  }

  private stopShortCallCheck(): void {
    this.shortCallCheckSub?.unsubscribe();
    this.shortCallCheckSub = null;
    this.checkingCall = false;
    this.loading = false;
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.refusalSnack?.dismiss();
    this.stopShortCallCheck();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  /**
   * Reads the patient's clinic switch once. Off — every clinic not switched on — and nothing else
   * happens: no care plan is read and no row changes.
   */
  private setUpCarePlanTime(): void {
    const clientId = this.patientService.currentPatientService?.client_responsible_id;
    this.clientsService
      .isCcmCarePlanTimeEnabled$(clientId)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((enabled) => {
        if (enabled) {
          this.turnOnCarePlanTime();
        }
      });
  }

  /** Every CCM row gets its picker; the care plans load with the first one. */
  private turnOnCarePlanTime(): void {
    this.carePlanTimeOn = true;
    this.times.controls.forEach((row) => this.syncCarePlanControl(row));
  }

  /**
   * Loads the patient's care plans once for every CCM row and, on an edit, the picks saved on the entry.
   * Validity changes here, in the load's callback and out of change detection: each row's control was
   * created with a validator that already reads "not loaded", so the picker attaching to it changes
   * nothing. A reload keeps the picks made so far, and replaces a load still in flight so an older answer
   * cannot land last.
   */
  private loadCarePlans(): void {
    if (this.destroyed) {
      return;
    }
    this.carePlanLoad?.unsubscribe();
    this.carePlanStatus = 'loading';
    this.revalidateCarePlanControls();
    const patientId = this.data?.time?.patient_id ?? this.patientService.currentPatientServiceID;
    this.carePlanLoad = this.carePlanWorkService
      .loadPicker$(patientId, this.data?.isNew ? undefined : this.data?.id)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: ({ plans, value, record }) => {
          this.carePlanPlans = plans;
          if (!this.data?.isNew && !this.carePlanPrefilled) {
            this.carePlanPrefilled = true;
            this.carePlanRequiredOnEditedRow = !(this.wasCcmBeforeEdit() && record === null);
            if (!this.restoredCarePlanWork) {
              this.editedTimeRow?.get('care_plan_work')?.setValue(value);
            }
          }
          this.carePlanStatus = 'ready';
          this.revalidateCarePlanControls();
        },
        error: () => {
          this.carePlanStatus = 'error';
          this.revalidateCarePlanControls();
        },
      });
  }

  /**
   * The server's reading of "was CCM": the stored categories, or the billing category for entries old
   * enough to have none (the dialog hands those over as `{}`).
   */
  private wasCcmBeforeEdit(): boolean {
    const categories = this.data?.time?.categories;
    return categories && Object.keys(categories).length > 0
      ? categories.ccm === true
      : this.data?.time?.category === 'ccm';
  }

  /**
   * On while the clinic is switched on and the row is CCM. Otherwise disabled — out of the form's validity
   * and value — but not reset, so unticking and re-ticking CCM keeps the picks. The first row turned on
   * loads the care plans: a form with no CCM row never reads them.
   */
  private syncCarePlanControl(row: AbstractControl): void {
    const control = row.get('care_plan_work');
    const wanted = this.carePlanTimeOn && row.get('categories.ccm')?.value === true;
    if (wanted && control.disabled) {
      control.enable();
      if (!this.carePlanLoad) {
        this.loadCarePlans();
      }
    } else if (!wanted && control.enabled) {
      control.disable();
    }
  }

  private revalidateCarePlanControls(): void {
    this.times.controls.forEach((row) => row.get('care_plan_work')?.updateValueAndValidity());
  }

  /**
   * The picker's own rule, attached in code so the row's status is right before the picker renders. It
   * counts the picks the picker counts — those whose goal is on screen — so the two never disagree.
   */
  private carePlanValidator(editedRow: boolean): ValidatorFn {
    return (control: AbstractControl) =>
      this.carePlanStatus !== 'ready'
        ? { carePlansNotLoaded: true }
        : validateCarePlanWork(this.carePlanPlans, this.visibleWork(control.value), {
            required: this.requiresPicks(editedRow),
          });
  }

  /** A row's items with only the picks whose goal is on screen, as the picker counts and sends them. */
  private visibleWork(work: CarePlanWorkRequest | null): CarePlanWorkRequest {
    const value = work ?? emptyCarePlanWork();
    return {
      plans: visiblePicks(this.carePlanPlans, value.plans ?? []),
      care_coordination: value.care_coordination ?? null,
      other: value.other ?? null,
    };
  }

  private requiresPicks(editedRow: boolean): boolean {
    return !editedRow || this.carePlanRequiredOnEditedRow || this.serverRequiresPicks;
  }

  /**
   * Some rows were not saved. The rows that did save leave the form — a retry must not save them twice —
   * and the form stays open to fix the rest. When every failure is a care plan or clinical note refusal,
   * the refusal is shown and the picker reacts to it; any other failure shows the server's reason, and on
   * a form that must be saved also offers Cancel, so the nurse can keep the minutes when a retry keeps
   * failing.
   */
  private handleSaveFailures(outcomes: ClinicalTimeSaveOutcome[], rows: (AbstractControl | undefined)[]): void {
    const failures = outcomes.filter((outcome) => !outcome.saved);
    const codes = failures.map(
      (failure) => this.carePlanRefusalCode(failure.error) ?? this.clinicalNoteRefusalCode(failure.error)
    );
    const unexpected = failures.find((_failure, index) => !codes[index]);
    // Only CHANGED can pass on a retry: the note was written meanwhile and is read again.
    if (
      codes.some(
        (code) => (CLINICAL_NOTE_ERROR_CODES as readonly string[]).includes(code) && code !== 'CLINICAL_NOTE_CHANGED'
      )
    ) {
      this.noteRefusedForGood = true;
    }
    const savedRows = rows.filter((row, index) => row && outcomes[index]?.saved);
    savedRows.forEach((row) => {
      const index = this.times.controls.indexOf(row);
      if (index >= 0) {
        this.times.removeAt(index);
      }
    });
    if (savedRows.length) {
      this.patientService.notifyClinicalTimeChange(this.data.isNew ? 'added' : 'modified');
    }
    this.loading = false;
    if (unexpected) {
      if (savedRows.length) {
        this.getCurrentMonthlyTime();
      }
      if (this.data?.required) {
        this.saveFailed = true;
      }
      const reason = serverErrorMessage(unexpected.error, TIME_SAVE_FAILED_MESSAGE);
      const message = savedRows.length ? partialTimeSaveMessage(savedRows.length, outcomes.length, reason) : reason;
      this.refusalSnack?.dismiss();
      this.snackBar.genericSnackBar(message, ['error-snackbar'], 8000, 'OK');
    } else {
      this.showRefusal(failures[0].error as HttpErrorResponse, codes[0], savedRows.length);
    }
    if (codes.includes('REQUIRED')) {
      this.serverRequiresPicks = true;
      this.turnOnCarePlanTime();
    }
    if (codes.some((code) => CARE_PLAN_RELOAD_CODES.includes(code as CarePlanWorkErrorCode))) {
      this.loadCarePlans();
    } else {
      this.revalidateCarePlanControls();
    }
  }

  /** The code of a care plan refusal: a 400 from welby-micro-service carrying one of its codes. */
  private carePlanRefusalCode(error: unknown): CarePlanWorkErrorCode | undefined {
    if (!(error instanceof HttpErrorResponse) || error.status !== 400) {
      return undefined;
    }
    const code = error.error?.code;
    return CARE_PLAN_WORK_ERROR_CODES.includes(code) ? code : undefined;
  }

  private plainTextPreview(html: string): string {
    // Paragraphs, list items and line breaks become spaces, so the words either side of them stay apart.
    const content = html.replace(/<\/(p|div|li|h[1-6])>|<br\s*\/?>/gi, ' ');
    const text = (new DOMParser().parseFromString(content, 'text/html').body.textContent ?? '')
      .replace(/\s+/g, ' ')
      .trim();
    return text.length > NOTE_PREVIEW_LENGTH ? `${text.slice(0, NOTE_PREVIEW_LENGTH).trimEnd()}…` : text;
  }

  /** The code of a clinical note refusal: a 400 from welby-micro-service when the note is missing or cannot be linked. */
  private clinicalNoteRefusalCode(error: unknown): ClinicalNoteErrorCode | undefined {
    if (!(error instanceof HttpErrorResponse) || error.status !== 400) {
      return undefined;
    }
    const code = error.error?.code;
    return (CLINICAL_NOTE_ERROR_CODES as readonly string[]).includes(code) ? code : undefined;
  }

  /**
   * Stays until dismissed, as the nurse reads it while fixing the row, and goes with the form. A refusal
   * that lands once the form is closed only says what was lost: there is no row left to fix.
   */
  private showRefusal(
    error: HttpErrorResponse,
    code: CarePlanWorkErrorCode | ClinicalNoteErrorCode,
    savedRows: number
  ): void {
    const fallback = (CLINICAL_NOTE_ERROR_CODES as readonly string[]).includes(code)
      ? 'The time could not be saved with its clinical note.'
      : 'The care plan items could not be saved.';
    const reason = typeof error.error?.message === 'string' ? error.error.message : fallback;
    if (this.destroyed) {
      const lost = savedRows ? 'Some of the time was not saved.' : 'The time was not saved.';
      const next = this.data?.isNew ? 'Add it again from the time form.' : 'Edit the entry again.';
      this.snackBar.genericSnackBar(`${lost} ${reason} ${next}`, ['error-snackbar'], 0, 'Dismiss');
      return;
    }
    const saved = savedRows ? ' The other time rows were saved.' : '';
    const advice = CARE_PLAN_REOPEN_CODES.includes(code as CarePlanWorkErrorCode)
      ? ' Note the minutes, close the time form and open it again. If it keeps happening, report it to support.'
      : '';
    this.refusalSnack = this.snackBar.genericSnackBar(`${reason}${saved}${advice}`, ['error-snackbar'], 0, 'Dismiss');
  }
}
